/**
 * Small inline icons for portal rails and tiles.
 * Stroke inherits `currentColor` so active/idle states stay in CSS.
 */
import { useState, type CSSProperties, type ReactNode } from "react";

export function Icon({
  children, size = 20, label,
}: { children: ReactNode; size?: number; label?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      aria-label={label}
    >
      {children}
    </svg>
  );
}

export const Icons = {
  home: (
    <Icon>
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1z" />
    </Icon>
  ),
  discover: (
    <Icon>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Icon>
  ),
  work: (
    <Icon>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7" />
    </Icon>
  ),
  submissions: (
    <Icon>
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v6h6" />
      <path d="M9 13h6M9 17h4" />
    </Icon>
  ),
  performance: (
    <Icon>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M8 15v-4M12 15V8M16 15v-7" />
    </Icon>
  ),
  earnings: (
    <Icon>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v10M9.5 9.5c.6-.8 1.6-1.2 2.5-1.2 1.4 0 2.5.8 2.5 2.2S13.4 12.5 12 12.5 9.5 13.5 9.5 15c0 1.3 1.1 2.2 2.5 2.2.9 0 1.9-.4 2.5-1.2" />
    </Icon>
  ),
  payouts: (
    <Icon>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h3" />
    </Icon>
  ),
  campaigns: (
    <Icon>
      <rect x="4" y="4" width="7" height="7" rx="1.4" />
      <rect x="13" y="4" width="7" height="7" rx="1.4" />
      <rect x="4" y="13" width="7" height="7" rx="1.4" />
      <rect x="13" y="13" width="7" height="7" rx="1.4" />
    </Icon>
  ),
  views: (
    <Icon>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ),
  connect: (
    <Icon>
      <circle cx="7" cy="12" r="2.4" />
      <circle cx="17" cy="7" r="2.4" />
      <circle cx="17" cy="17" r="2.4" />
      <path d="M9.2 11.2 14.8 8.2M9.2 12.8 14.8 15.8" />
    </Icon>
  ),
  more: (
    <Icon>
      <circle cx="5" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" />
    </Icon>
  ),
  social: (
    <Icon>
      <circle cx="7" cy="8" r="2.2" />
      <circle cx="17" cy="8" r="2.2" />
      <circle cx="12" cy="17" r="2.2" />
      <path d="M8.8 9.4 10.6 15M15.2 9.4 13.4 15" />
    </Icon>
  ),
  member: (
    <Icon size={14}>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5.5 19c.8-3.2 3.1-5 6.5-5s5.7 1.8 6.5 5" />
    </Icon>
  ),
  approval: (
    <Icon size={14}>
      <circle cx="12" cy="12" r="8" />
      <path d="m8.5 12.2 2.3 2.3 4.7-5" />
    </Icon>
  ),
  arrow: (
    <Icon>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Icon>
  ),
  profile: (
    <Icon>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5.5 19c.8-3.2 3.1-5 6.5-5s5.7 1.8 6.5 5" />
    </Icon>
  ),
  ledger: (
    <Icon>
      <path d="M6 4h12a1 1 0 0 1 1 1v15l-3-1.6-3 1.6-3-1.6-3 1.6V5a1 1 0 0 1 1-1z" />
      <path d="M9 9h6M9 13h6" />
    </Icon>
  ),
  refund: (
    <Icon>
      <path d="M8 10H4V6" />
      <path d="M4 10a8 8 0 1 0 2.2-5.6" />
    </Icon>
  ),
  dispute: (
    <Icon>
      <path d="M12 3 4 7v5c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V7z" />
      <path d="M12 8v5M12 16.2h.01" />
    </Icon>
  ),
  adjust: (
    <Icon>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.2 6.2l1.4 1.4M16.4 16.4l1.4 1.4M17.8 6.2l-1.4 1.4M7.6 16.4 6.2 17.8" />
    </Icon>
  ),
  settings: (
    <Icon>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.6 1.1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </Icon>
  ),
  shield: (
    <Icon>
      <path d="M12 3 4 7v5c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V7z" />
      <path d="m9 12 2 2 4-4" />
    </Icon>
  ),
  support: (
    <Icon>
      <path d="M8 10a4 4 0 0 1 8 0v2a3 3 0 0 0 3 3h0v1H5v-1a3 3 0 0 0 3-3z" />
      <path d="M9 19h6" />
    </Icon>
  ),
  audit: (
    <Icon>
      <path d="M8 4h8l3 3v13H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path d="M10 11h6M10 15h4" />
    </Icon>
  ),
};

const avatarStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  background: "var(--accent)",
  color: "var(--accent-fg)",
  display: "grid",
  placeItems: "center",
};

/** Lime circular mark used in portal headers. */
export function CreatorAvatarMark({ size = 56 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{ ...avatarStyle, width: size, height: size, flex: `0 0 ${size}px` }}
    >
      <svg width={size * 0.68} height={size * 0.68} viewBox="0 0 24 24" fill="currentColor">
        <path d="M5.2 8.2c0-1.2.8-2.2 2.4-2.8C8.4 3.8 9.8 3 12 3s3.6.8 4.4 2.4c1.6.6 2.4 1.6 2.4 2.8 0 .4-.2.7-.6.7H5.8c-.4 0-.6-.3-.6-.7z" />
        <circle cx="12" cy="13" r="3.1" />
        <path d="M6.2 20.4c.7-2.6 2.8-4 5.8-4s5.1 1.4 5.8 4c.1.4-.2.8-.6.8H6.8c-.4 0-.7-.4-.6-.8z" />
      </svg>
    </span>
  );
}

/** Photo when one exists; the lime mark otherwise. */
export function CreatorAvatar({
  src, name, size = 56,
}: { src?: string | null | undefined; name?: string | undefined; size?: number }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (!src || failedSrc === src) return <CreatorAvatarMark size={size} />;
  return (
    <img
      src={src}
      alt={name ?? ""}
      width={size}
      height={size}
      onError={() => setFailedSrc(src)}
      style={{
        width: size, height: size, flex: `0 0 ${size}px`,
        borderRadius: "50%", objectFit: "cover",
        background: "var(--surface-2)", display: "block",
      }}
    />
  );
}
